import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { createBreadcrumbSchema, getBreadcrumbsForPath } from "@/utils/structuredData";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  structuredData?: object | object[];
  robots?: string;
  author?: string;
  language?: string;
  includeBreadcrumbs?: boolean;
}

const SEO = ({
  title = "Interesting Technologies — ISO 9001:2015 Certified Software Company",
  description = "ISO 9001:2015 certified AI-first software company. AI/ML powered custom software solutions for consumers and enterprises. Transform your business with AI-integrated solutions.",
  keywords = "ISO 9001:2015 certified software company, custom software development, AI integration, mobile app development, cloud solutions, data engineering, machine learning, data science, artificial intelligence, agartala, tripura, india, top software company, flutter, iOS apps, android app development, AI company, LLM development, custom ai chatbot development, gcp, aws, azure, google, amazon web services, microsoft",
  canonical,
  ogTitle,
  ogDescription,
  ogImage = "https://interestech.com/itpl-og.webp",
  ogUrl,
  twitterTitle,
  twitterDescription,
  structuredData,
  robots = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  author = "Interesting Technologies Private Limited",
  language = "en",
  includeBreadcrumbs = true,
}: SEOProps) => {
  const location = useLocation();
  useEffect(() => {
    // Update title
    document.title = title;

    // Update meta tags
    updateMetaTag("description", description);
    updateMetaTag("keywords", keywords);
    updateMetaTag("robots", robots);
    updateMetaTag("author", author);
    updateMetaTag("language", language);

    // Update Open Graph tags
    updateMetaTag("og:title", ogTitle || title, "property");
    updateMetaTag("og:description", ogDescription || description, "property");
    updateMetaTag("og:image", ogImage, "property");
    updateMetaTag("og:url", ogUrl || canonical || window.location.href, "property");
    updateMetaTag("og:type", "website", "property");
    updateMetaTag("og:site_name", "Interesting Technologies", "property");

    // Update Twitter tags
    updateMetaTag("twitter:card", "summary_large_image");
    updateMetaTag("twitter:site", "@interestech");
    updateMetaTag("twitter:creator", "@interestech");
    updateMetaTag("twitter:title", twitterTitle || title);
    updateMetaTag("twitter:description", twitterDescription || description);
    updateMetaTag("twitter:image", ogImage);
    updateMetaTag("twitter:image:alt", `${title} - Social Preview`);

    // Update canonical URL
    if (canonical) {
      updateCanonicalTag(canonical);
    }

    // Add structured data
    if (structuredData) {
      updateStructuredData(structuredData, includeBreadcrumbs ? location.pathname : null);
    } else if (includeBreadcrumbs) {
      // Add breadcrumb schema even if no other structured data
      updateStructuredData(null, location.pathname);
    }
  }, [
    title,
    description,
    keywords,
    canonical,
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl,
    twitterTitle,
    twitterDescription,
    structuredData,
    robots,
    author,
    language,
    includeBreadcrumbs,
    location.pathname,
  ]);

  const updateMetaTag = (name: string, content: string, attribute: string = "name") => {
    let element = document.querySelector(`meta[${attribute}="${name}"]`);
    if (element) {
      element.setAttribute("content", content);
    } else {
      element = document.createElement("meta");
      element.setAttribute(attribute, name);
      element.setAttribute("content", content);
      document.head.appendChild(element);
    }
  };

  const updateCanonicalTag = (url: string) => {
    let element = document.querySelector('link[rel="canonical"]');
    if (element) {
      element.setAttribute("href", url);
    } else {
      element = document.createElement("link");
      element.setAttribute("rel", "canonical");
      element.setAttribute("href", url);
      document.head.appendChild(element);
    }
  };

  const updateStructuredData = (data: object | object[] | null, pathname: string | null) => {
    // Remove existing structured data scripts (except breadcrumb which we'll update)
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach((script) => {
      script.remove();
    });

    const allData: object[] = [];

    // Add provided structured data
    if (data) {
      const dataArray = Array.isArray(data) ? data : [data];
      allData.push(...dataArray);
    }

    // Add breadcrumb schema if pathname provided
    if (pathname) {
      const breadcrumbs = getBreadcrumbsForPath(pathname);
      if (breadcrumbs.length > 1) {
        const breadcrumbSchema = createBreadcrumbSchema(breadcrumbs);
        allData.push(breadcrumbSchema);
      }
    }

    // Add all structured data to head
    allData.forEach((item, index) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = `structured-data-${index}`;
      script.textContent = JSON.stringify(item);
      document.head.appendChild(script);
    });
  };

  return null;
};

export default SEO;
